import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import type { AxiosResponse } from 'axios'
import { useMutation } from '@tanstack/react-query'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Flex,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Stack,
  Text
} from '@chakra-ui/react'
import { formatISODate } from '@homevest/utils/dates'
import type {
  CreateOrphanedUtilityChargebackPayload,
  CreateOrphanedUtilityChargebackResponse
} from '@homevest/types/utilities'
import { useUtilityTypesQuery } from 'graphql/generated'
import axios from 'lib/axios'
import { BooksLockedNotice, useBuildiumPostingGuard } from 'lib/buildiumBooksLock'
import startCase from 'lodash/startCase'
import moment from 'moment'
import BuildiumPostingSummary from '../BuildiumPostingSummary'
import {
  DEFAULT_FEE_PERCENTAGE,
  calculateChargebackAmount,
  getEligibleUtilityTypes,
  getFeePercentageError,
  getOriginalAmountError,
  getOrphanedChargebackErrorMessage,
  getRequiredDateError,
  getServicePeriodError,
  isAmbiguousSubmissionError
} from './helpers'
import type { OrphanedUtilityChargebackModalProps } from './types'
import UtilityTypeSelect from './UtilityTypeSelect'

type CreatedLiability = CreateOrphanedUtilityChargebackResponse['rental_liability']

type FormField =
  | 'utilityType'
  | 'originalAmount'
  | 'feePercentage'
  | 'postingDate'
  | 'serviceStartDate'
  | 'serviceEndDate'

const createUntouchedFields = (): Record<FormField, boolean> => ({
  utilityType: false,
  originalAmount: false,
  feePercentage: false,
  postingDate: false,
  serviceStartDate: false,
  serviceEndDate: false
})

export default function OrphanedUtilityChargebackModal({
  finalLiabilityDate,
  isOpen,
  occupancyDate,
  onAmbiguousOutcome,
  onBack,
  onClose,
  onSuccess,
  rentalId
}: OrphanedUtilityChargebackModalProps) {
  const [originalAmount, setOriginalAmount] = useState('')
  const [date, setDate] = useState(formatISODate(new Date()))
  const [utilityTypeId, setUtilityTypeId] = useState('')
  const [serviceStartDate, setServiceStartDate] = useState('')
  const [serviceEndDate, setServiceEndDate] = useState('')
  const [feePercentage, setFeePercentage] = useState(DEFAULT_FEE_PERCENTAGE)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [isOutcomeAmbiguous, setIsOutcomeAmbiguous] = useState(false)
  const [ambiguousRefreshFailed, setAmbiguousRefreshFailed] = useState(false)
  const [createdLiability, setCreatedLiability] = useState<CreatedLiability>()
  const [touchedFields, setTouchedFields] = useState(createUntouchedFields)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [isSubmittingLocally, setIsSubmittingLocally] = useState(false)
  const amountInputRef = useRef<HTMLInputElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const feePercentageInputRef = useRef<HTMLInputElement>(null)
  const postingDateInputRef = useRef<HTMLInputElement>(null)
  const serviceEndDateInputRef = useRef<HTMLInputElement>(null)
  const serviceStartDateInputRef = useRef<HTMLInputElement>(null)
  const utilityTypeSelectRef = useRef<HTMLSelectElement>(null)
  const submissionInFlightRef = useRef(false)

  const utilityTypesQueryOptions = useMemo(
    () => ({
      pause: !isOpen,
      requestPolicy: 'network-only' as const,
      context: {
        suspense: false
      }
    }),
    [isOpen]
  )
  const [
    { data, fetching: utilityTypesFetching, error: utilityTypesError },
    reexecuteUtilityTypes
  ] = useUtilityTypesQuery(utilityTypesQueryOptions)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setOriginalAmount('')
    setDate(formatISODate(new Date()))
    setUtilityTypeId('')
    setServiceStartDate('')
    setServiceEndDate('')
    setFeePercentage(DEFAULT_FEE_PERCENTAGE)
    setErrorMessage(undefined)
    setIsOutcomeAmbiguous(false)
    setAmbiguousRefreshFailed(false)
    setCreatedLiability(undefined)
    setTouchedFields(createUntouchedFields())
    setHasAttemptedSubmit(false)
    setIsSubmittingLocally(false)
    submissionInFlightRef.current = false
  }, [isOpen, rentalId])

  const utilityTypes = useMemo(() => data?.utility_types ?? [], [data?.utility_types])
  const eligibleUtilityTypes = useMemo(() => getEligibleUtilityTypes(utilityTypes), [utilityTypes])

  useEffect(() => {
    if (
      isOpen &&
      !createdLiability &&
      !utilityTypesFetching &&
      !utilityTypesError &&
      eligibleUtilityTypes.length > 0
    ) {
      const focusTimeout = window.setTimeout(() => {
        utilityTypeSelectRef.current?.focus({ preventScroll: true })
      })

      return () => window.clearTimeout(focusTimeout)
    }

    return undefined
  }, [
    createdLiability,
    eligibleUtilityTypes.length,
    isOpen,
    utilityTypesError,
    utilityTypesFetching
  ])

  const selectedUtilityType = eligibleUtilityTypes.find(
    (utilityType) => utilityType.id === utilityTypeId
  )
  const selectedUtilityTypeLabel = selectedUtilityType
    ? startCase(selectedUtilityType.name)
    : undefined
  const amountError = getOriginalAmountError(originalAmount)
  const feePercentageError = getFeePercentageError(feePercentage)
  const postingDateError = getRequiredDateError(date, 'Posting date')
  const serviceStartDateError = getRequiredDateError(serviceStartDate, 'Service start date')
  const serviceEndDateError = getRequiredDateError(serviceEndDate, 'Service end date')
  const servicePeriodError = getServicePeriodError({
    finalLiabilityDate,
    occupancyDate,
    serviceEndDate,
    serviceStartDate
  })
  const calculatedAmount = calculateChargebackAmount(originalAmount, feePercentage)
  const {
    booksLock,
    hasValidPostingDate,
    isBooksLockLoading,
    isBooksLockError,
    isBuildiumBooksLocked,
    retryBooksLock
  } = useBuildiumPostingGuard({
    date,
    enabled: isOpen && !createdLiability,
    rentalId
  })

  const { mutateAsync: submitChargeback, isLoading } = useMutation<
    AxiosResponse<CreateOrphanedUtilityChargebackResponse>,
    unknown,
    CreateOrphanedUtilityChargebackPayload
  >({
    mutationFn: (payload) => axios.post('/admin/utility_chargebacks/orphaned', payload),
    retry: false
  })

  const isFormDisabled =
    isLoading || isSubmittingLocally || isOutcomeAmbiguous || utilityTypesFetching
  const isSubmissionUnavailable =
    isFormDisabled ||
    Boolean(createdLiability) ||
    utilityTypesFetching ||
    Boolean(utilityTypesError) ||
    eligibleUtilityTypes.length === 0 ||
    isBooksLockLoading ||
    isBooksLockError ||
    isBuildiumBooksLocked
  const canSubmit =
    !isFormDisabled &&
    !createdLiability &&
    !utilityTypesFetching &&
    !utilityTypesError &&
    Boolean(selectedUtilityType) &&
    !amountError &&
    !feePercentageError &&
    !postingDateError &&
    !serviceStartDateError &&
    !serviceEndDateError &&
    !servicePeriodError &&
    Boolean(calculatedAmount) &&
    hasValidPostingDate &&
    !isBooksLockLoading &&
    !isBooksLockError &&
    !isBuildiumBooksLocked

  const showErrorFor = (field: FormField) => hasAttemptedSubmit || touchedFields[field]
  const touchField = (field: FormField) =>
    setTouchedFields((currentFields) => ({ ...currentFields, [field]: true }))
  const clearEditableError = () => setErrorMessage(undefined)

  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault()
    setHasAttemptedSubmit(true)

    if (submissionInFlightRef.current) {
      return
    }

    if (!canSubmit) {
      let firstInvalidInput: React.RefObject<HTMLElement> | undefined

      if (!selectedUtilityType) {
        firstInvalidInput = utilityTypeSelectRef
      } else if (amountError) {
        firstInvalidInput = amountInputRef
      } else if (feePercentageError) {
        firstInvalidInput = feePercentageInputRef
      } else if (serviceStartDateError) {
        firstInvalidInput = serviceStartDateInputRef
      } else if (serviceEndDateError || servicePeriodError) {
        firstInvalidInput = serviceEndDateInputRef
      } else if (postingDateError || !hasValidPostingDate) {
        firstInvalidInput = postingDateInputRef
      }

      firstInvalidInput?.current?.focus()
      return
    }

    submissionInFlightRef.current = true
    setIsSubmittingLocally(true)
    setErrorMessage(undefined)

    let response: AxiosResponse<CreateOrphanedUtilityChargebackResponse>

    try {
      response = await submitChargeback({
        rental_id: rentalId,
        utility_type_id: utilityTypeId,
        amount: Number(originalAmount),
        date,
        service_start_date: serviceStartDate,
        service_end_date: serviceEndDate,
        fee_percentage: Number(feePercentage)
      })
    } catch (submissionError) {
      if (isAmbiguousSubmissionError(submissionError)) {
        setIsOutcomeAmbiguous(true)
        submissionInFlightRef.current = false
        setIsSubmittingLocally(false)

        try {
          await onAmbiguousOutcome()
        } catch (refreshError) {
          console.error(
            'Unable to refresh the ledger after an ambiguous orphaned chargeback outcome',
            refreshError
          )
          setAmbiguousRefreshFailed(true)
        }

        return
      }

      setErrorMessage(getOrphanedChargebackErrorMessage(submissionError))
      submissionInFlightRef.current = false
      setIsSubmittingLocally(false)
      return
    }

    submissionInFlightRef.current = false
    setIsSubmittingLocally(false)
    setCreatedLiability(response.data.rental_liability)

    try {
      await onSuccess([response.data.rental_liability.id])
    } catch (refreshError) {
      console.error(
        'Unable to refresh the ledger after creating an orphaned chargeback',
        refreshError
      )
    }
  }

  const handleClose = () => {
    if (submissionInFlightRef.current) {
      return
    }

    onClose()
  }

  const handleBack = () => {
    if (submissionInFlightRef.current) {
      return
    }

    onBack?.()
  }

  const rentalPeriodLabel = `${moment(occupancyDate).format('MMM D, YYYY')} – ${
    finalLiabilityDate ? moment(finalLiabilityDate).format('MMM D, YYYY') : 'ongoing'
  }`

  return (
    <Modal
      initialFocusRef={closeButtonRef}
      size='3xl'
      isOpen={isOpen}
      onClose={handleClose}
      closeOnEsc={!isSubmittingLocally && !isLoading}
      closeOnOverlayClick={!isSubmittingLocally && !isLoading}
      motionPreset='none'
      scrollBehavior='inside'
    >
      <ModalOverlay bg='blackAlpha.500' />
      <ModalContent
        as='form'
        maxH='calc(100% - 2rem)'
        mx={4}
        my={4}
        noValidate
        onSubmit={handleSubmit}
      >
        <ModalHeader pb={2}>Create Utility Chargeback</ModalHeader>
        <ModalCloseButton ref={closeButtonRef} isDisabled={isSubmittingLocally || isLoading} />
        <ModalBody>
          {!createdLiability && (
            <Text color='gray.600' mb={4}>
              Use this when there is no matching utility bill.
            </Text>
          )}
          {createdLiability ? (
            <Alert status='success' borderRadius='md'>
              <AlertIcon />
              Utility chargeback created for ${createdLiability.amount}.
            </Alert>
          ) : (
            <Stack spacing={4}>
              <UtilityTypeSelect
                error={Boolean(utilityTypesError)}
                fetching={utilityTypesFetching}
                isDisabled={isFormDisabled}
                onBlur={() => touchField('utilityType')}
                onChange={(nextUtilityTypeId) => {
                  setUtilityTypeId(nextUtilityTypeId)
                  touchField('utilityType')
                  clearEditableError()
                }}
                onRetry={() => reexecuteUtilityTypes({ requestPolicy: 'network-only' })}
                selectRef={utilityTypeSelectRef}
                showValidationError={showErrorFor('utilityType')}
                utilityTypeId={utilityTypeId}
                utilityTypes={utilityTypes}
              />

              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={4}>
                <FormControl
                  isRequired
                  isInvalid={showErrorFor('originalAmount') && Boolean(amountError)}
                  isDisabled={isFormDisabled}
                >
                  <FormLabel>Original bill amount</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents='none' color='gray.500'>
                      $
                    </InputLeftElement>
                    <NumberInput
                      clampValueOnBlur={false}
                      min={0.01}
                      precision={2}
                      value={originalAmount}
                      onChange={(nextAmount) => {
                        setOriginalAmount(nextAmount)
                        clearEditableError()
                      }}
                      width='full'
                    >
                      <NumberInputField
                        ref={amountInputRef}
                        aria-label='Original bill amount'
                        inputMode='decimal'
                        onBlur={() => touchField('originalAmount')}
                        paddingLeft={8}
                        placeholder='0.00'
                      />
                    </NumberInput>
                  </InputGroup>
                  <FormErrorMessage>{amountError}</FormErrorMessage>
                </FormControl>

                <FormControl
                  isRequired
                  isInvalid={showErrorFor('feePercentage') && Boolean(feePercentageError)}
                  isDisabled={isFormDisabled}
                >
                  <FormLabel>Fee percentage</FormLabel>
                  <InputGroup>
                    <NumberInput
                      clampValueOnBlur={false}
                      min={0}
                      max={100}
                      precision={3}
                      step={0.001}
                      value={feePercentage}
                      onChange={(nextFeePercentage) => {
                        setFeePercentage(nextFeePercentage)
                        clearEditableError()
                      }}
                      width='full'
                    >
                      <NumberInputField
                        ref={feePercentageInputRef}
                        aria-label='Fee percentage'
                        inputMode='decimal'
                        onBlur={() => touchField('feePercentage')}
                        paddingRight={8}
                      />
                    </NumberInput>
                    <InputRightElement pointerEvents='none' color='gray.500'>
                      %
                    </InputRightElement>
                  </InputGroup>
                  <FormHelperText>
                    Added to the original bill amount. Default: 0.867%.
                  </FormHelperText>
                  <FormErrorMessage>{feePercentageError}</FormErrorMessage>
                </FormControl>
              </Grid>

              <Box>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={4}>
                  <FormControl
                    isRequired
                    isInvalid={showErrorFor('serviceStartDate') && Boolean(serviceStartDateError)}
                    isDisabled={isFormDisabled}
                  >
                    <FormLabel>Service start date</FormLabel>
                    <Input
                      ref={serviceStartDateInputRef}
                      aria-label='Service start date'
                      type='date'
                      value={serviceStartDate}
                      onBlur={() => touchField('serviceStartDate')}
                      onChange={(event) => {
                        setServiceStartDate(event.target.value)
                        clearEditableError()
                      }}
                    />
                    <FormErrorMessage>{serviceStartDateError}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isRequired
                    isInvalid={
                      (showErrorFor('serviceEndDate') && Boolean(serviceEndDateError)) ||
                      ((showErrorFor('serviceStartDate') || showErrorFor('serviceEndDate')) &&
                        Boolean(servicePeriodError))
                    }
                    isDisabled={isFormDisabled}
                  >
                    <FormLabel>Service end date</FormLabel>
                    <Input
                      ref={serviceEndDateInputRef}
                      aria-label='Service end date'
                      type='date'
                      value={serviceEndDate}
                      onBlur={() => touchField('serviceEndDate')}
                      onChange={(event) => {
                        setServiceEndDate(event.target.value)
                        clearEditableError()
                      }}
                    />
                    <FormErrorMessage>{serviceEndDateError ?? servicePeriodError}</FormErrorMessage>
                  </FormControl>
                </Grid>
                <Text color='gray.600' fontSize='sm' mt={2}>
                  Service dates must overlap the rental period: {rentalPeriodLabel}.
                </Text>
              </Box>

              <Box maxW={{ md: 'calc(50% - 0.5rem)' }}>
                <FormControl
                  isRequired
                  isInvalid={
                    (showErrorFor('postingDate') && Boolean(postingDateError)) ||
                    isBuildiumBooksLocked
                  }
                  isDisabled={isFormDisabled}
                >
                  <FormLabel>Posting date</FormLabel>
                  <Input
                    ref={postingDateInputRef}
                    aria-label='Posting date'
                    type='date'
                    value={date}
                    onBlur={() => touchField('postingDate')}
                    onChange={(event) => {
                      setDate(event.target.value)
                      clearEditableError()
                    }}
                  />
                  <FormErrorMessage>{postingDateError}</FormErrorMessage>
                </FormControl>
              </Box>

              {isBooksLockError && (
                <Alert status='error' borderRadius='md' alignItems='center'>
                  <AlertIcon />
                  <Text flex={1}>Unable to verify Buildium’s locked date.</Text>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    onClick={() => retryBooksLock()}
                  >
                    Retry
                  </Button>
                </Alert>
              )}
              {isBuildiumBooksLocked && (
                <BooksLockedNotice lockDate={booksLock?.lockDate} isError={isBooksLockError} />
              )}

              <BuildiumPostingSummary
                amount={calculatedAmount ? Number(calculatedAmount) : Number.NaN}
                date={date}
                dateError={postingDateError}
                dateLabel='Posting date'
                entryDescription='Chargeback without a utility bill'
                extraRows={[
                  {
                    label: 'Service',
                    value:
                      serviceStartDate && serviceEndDate
                        ? `${moment(serviceStartDate).format('M/D/YYYY')} – ${moment(
                            serviceEndDate
                          ).format('M/D/YYYY')}`
                        : 'Not set'
                  },
                  {
                    label: 'Fee',
                    value: feePercentage ? `${feePercentage}%` : 'Not set'
                  }
                ]}
                isBooksLockError={isBooksLockError}
                isBooksLockLoading={isBooksLockLoading}
                isBuildiumBooksLocked={isBuildiumBooksLocked}
                lockDate={booksLock?.lockDate}
                typeLabel={selectedUtilityTypeLabel}
              />
            </Stack>
          )}
        </ModalBody>
        <ModalFooter flexDirection='column' alignItems='stretch' gap={4}>
          {!createdLiability && (isOutcomeAmbiguous || errorMessage) && (
            <Stack spacing={3}>
              {isOutcomeAmbiguous && (
                <Alert status='warning' alignItems='start' borderRadius='md'>
                  <AlertIcon mt={1} />
                  <Stack spacing={1}>
                    <Text fontWeight='semibold'>We couldn’t confirm whether this was created.</Text>
                    <Text>
                      Check the refreshed ledger for {selectedUtilityTypeLabel ?? 'this utility'}, $
                      {calculatedAmount ?? 'the calculated amount'}, dated {date} before trying
                      again.
                    </Text>
                    {ambiguousRefreshFailed && (
                      <Text>
                        The ledger did not refresh automatically. Close this window and refresh the
                        rental first.
                      </Text>
                    )}
                  </Stack>
                </Alert>
              )}

              {errorMessage && (
                <Alert status='error' borderRadius='md'>
                  <AlertIcon />
                  {errorMessage}
                </Alert>
              )}
            </Stack>
          )}

          <Flex justifyContent={onBack && !createdLiability ? 'space-between' : 'flex-end'}>
            {createdLiability ? (
              <Button type='button' colorScheme='teal' onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                {onBack && (
                  <Button
                    type='button'
                    variant='outline'
                    onClick={handleBack}
                    isDisabled={isSubmittingLocally || isLoading}
                  >
                    Back
                  </Button>
                )}
                <HStack>
                  {!onBack && (
                    <Button
                      type='button'
                      variant='outline'
                      onClick={handleClose}
                      isDisabled={isSubmittingLocally || isLoading}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    type='submit'
                    colorScheme='teal'
                    isDisabled={isSubmissionUnavailable}
                    isLoading={isSubmittingLocally || isLoading}
                  >
                    Create Chargeback
                  </Button>
                </HStack>
              </>
            )}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
